import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/i18n";

const IMG_ARKANA = "/assets/arkana.png";
const IMG_MINIVAN =
  "https://images.pexels.com/photos/36377055/pexels-photo-36377055.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_ELECTRIC =
  "https://images.unsplash.com/photo-1700411881984-971bc29083bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxibGFjayUyMHRlc2xhJTIwbW9kZWwlMjAzfGVufDB8fHx8MTc5MDI0MzU5Mnww&ixlib=rb-4.1.0&q=85";

const META = [
  { id: "berlina", img: IMG_ARKANA },
  { id: "minivan", img: IMG_MINIVAN },
  { id: "elettrica", img: IMG_ELECTRIC },
];

export const Fleet = () => {
  const { t } = useLang();
  const f = t.fleet;
  const items = f.items.map((item, i) => ({ ...item, img: META[i].img }));
  const [activeId, setActiveId] = useState("berlina");
  const active = items.find((x) => x.id === activeId) || items[0];

  return (
    <section id="flotta" className="border-y border-white/10 bg-[#0B0B10] py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow={f.eyebrow} title={f.title} accent={f.accent} />
        <div className="mb-10 flex flex-wrap gap-3">
          {items.map((item) => (
            <button
              key={item.id}
              data-testid={`fleet-tab-${item.id}`}
              onClick={() => setActiveId(item.id)}
              className={`rounded-full border px-6 py-3 font-display text-sm font-bold transition-all duration-300 ${
                active.id === item.id
                  ? "border-amber-400 bg-amber-400 text-black"
                  : "border-white/15 text-slate-300 hover:border-amber-400/50 hover:text-amber-300"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active.id}
                  src={active.img}
                  alt={active.label}
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="aspect-[16/10] w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#070709]/70 to-transparent" />
              <span className="absolute bottom-5 left-5 font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-300">
                {active.label}
              </span>
            </div>
          </Reveal>
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="font-display text-3xl font-extrabold text-white">{active.label}</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-400">{active.desc}</p>
              <ul className="mt-8 space-y-4">
                {active.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-slate-200">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400/15">
                      <Check className="h-3.5 w-3.5 text-amber-400" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
              <a
                href="tel:+41766344242"
                data-testid="fleet-call-cta"
                className="mt-10 inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-black transition-all duration-300 hover:bg-amber-300"
              >
                {f.cta}
              </a>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
